import { GridLayoutItem } from "@progress/kendo-react-layout";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConditionBuilder } from "@/components/ConditionBuilder/hooks/useConditionBuilder";
import {
  ConditionAttributeOption,
  ConditionOption,
  MatchWithOption,
} from "@/components/ConditionBuilder/types";
import { hasNonEmptyConditionValue } from "@/components/ConditionBuilder/utils/conditionTree";
import {
  FormDropDownListPaginate,
  FormInput,
  FormMultiSelect,
  FormTextArea,
} from "@/components/Forms";

interface ConditionValueFieldProps {
  fieldName: string;
  attributeFieldName: string;
  operatorFieldName: string;
  matchWithFieldName?: string;
}

const ConditionValueField: React.FC<ConditionValueFieldProps> = ({
  fieldName,
  attributeFieldName,
  operatorFieldName,
  matchWithFieldName,
}) => {
  const { t } = useTranslation();
  const { valueGetter, onChange, config } = useConditionBuilder();
  const methods = useFormContext();
  const attribute = valueGetter(attributeFieldName) as
    | ConditionAttributeOption
    | undefined;
  const operator = valueGetter(operatorFieldName) as
    | ConditionOption
    | undefined;
  // Selected Match With option — only read when the feature is enabled, so
  // non-enabled rows never touch a (possibly absent) matchWith path.
  const matchWith =
    config.enableMatchWith && matchWithFieldName
      ? (valueGetter(matchWithFieldName) as MatchWithOption | null | undefined)
      : undefined;

  // Always required when the row exists. To skip server rules entirely, the
  // user removes the row via the trash icon.
  const valueValidator = useCallback(
    (value: unknown) =>
      !hasNonEmptyConditionValue(value) ? t("validation.required") : undefined,
    [t],
  );

  // Belt-and-suspenders registration: same rationale as ConditionOperatorField.
  // useController inside the underlying Form* component registers rules, but
  // for deeply-nested array paths the rule has been observed to drop out of
  // RHF's re-validation tracking after the first trigger(), so the inline
  // error never clears when the user provides a valid value. Explicit
  // register here ensures _fields[name]._f.validate stays populated.
  useEffect(() => {
    methods.register(fieldName as never, {
      validate: (v: unknown) => {
        const msg = valueValidator(v);
        return typeof msg === "string" && msg.length > 0 ? msg : true;
      },
    });
  }, [methods, fieldName, valueValidator]);

  // Force re-validation on value change — same rationale as in
  // ConditionOperatorField. Use methods.watch (callback form) rather than
  // useWatch to avoid an infinite loop: useWatch returns a fresh deep clone
  // every render, so any object/array value would re-fire a useEffect on
  // every render.
  useEffect(() => {
    const subscription = methods.watch((_values, info) => {
      if (info?.name === fieldName && info?.type === "change") {
        void methods.trigger(fieldName as never);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, fieldName]);

  // Shared query with ConditionOperatorField — react-query deduplicates the
  // call so only one API request is made per attribute selection per row.
  const { data: resolvedMeta } = useQuery({
    queryKey: ["resolveAttribute", attribute?.id, attribute?.type],
    queryFn: () => config.resolveAttribute!(attribute!.id, attribute),
    enabled: !!attribute && !!config.resolveAttribute,
  });

  // Resolves value configuration for the selected (attribute, operator) pair.
  const { data: resolvedOperatorMeta } = useQuery({
    queryKey: ["resolveOperator", attribute?.id, attribute?.type, operator?.id],
    queryFn: () =>
      config.resolveOperator!(attribute!.id, operator!.id, attribute),
    enabled: !!attribute && !!operator && !!config.resolveOperator,
  });

  // Whether this row has any Match With options (same priority chain as
  // ConditionMatchWithField). Used to gate the Value field only when a Match
  // With selection is actually expected — rows with no options are not gated.
  const matchWithHasOptions =
    (
      resolvedOperatorMeta?.matchWithOptions ??
      resolvedMeta?.matchWithOptions ??
      attribute?.matchWithOptions ??
      []
    ).length > 0;

  // Priority chain (highest to lowest):
  //   selected Match With option (when enableMatchWith) > resolveOperator result
  //     > resolveAttribute result > per-attribute static > default
  const effectiveValueType =
    matchWith?.valueType ??
    resolvedOperatorMeta?.valueType ??
    resolvedMeta?.valueType ??
    attribute?.valueType ??
    "text";
  const effectiveFetchOptions =
    matchWith?.fetchOptions ??
    resolvedOperatorMeta?.fetchOptions ??
    resolvedMeta?.fetchOptions ??
    attribute?.fetchOptions;
  const effectivePaginatedFetchOptions =
    matchWith?.paginatedFetchOptions ??
    resolvedOperatorMeta?.paginatedFetchOptions ??
    resolvedMeta?.paginatedFetchOptions;
  const effectiveValueOptions =
    matchWith?.valueOptions ??
    resolvedOperatorMeta?.valueOptions ??
    resolvedMeta?.valueOptions ??
    attribute?.valueOptions;

  const isSelectType =
    effectiveValueType === "single-select" ||
    effectiveValueType === "multi-select";

  // Normalize: if the effective type resolves to a select type but the stored
  // value is a raw string, parse it into ConditionOption[]. Edit mode can seed
  // a string before attribute metadata loads.
  const rawValue = valueGetter(fieldName);
  const rawValueIsString = isSelectType && typeof rawValue === "string";
  useEffect(() => {
    if (!rawValueIsString) return;
    const str = rawValue as string;
    let parsed: ConditionOption[];
    if (effectiveValueType === "multi-select") {
      parsed = str
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
        .map(id => ({ id, name: id }));
    } else {
      parsed = str ? [{ id: str, name: str }] : [];
    }
    // Server-data normalization (raw string -> ConditionOption[]). Programmatic,
    // must NOT dirty the form.
    methods.setValue(
      fieldName as never,
      (parsed.length > 0 ? parsed : null) as never,
      { shouldDirty: false, shouldTouch: false, shouldValidate: false },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawValueIsString]);

  // Reset the value field when the selected attribute changes so stale
  // values from a previous attribute are never submitted.
  const prevAttributeId = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentId = attribute?.id;
    if (
      prevAttributeId.current !== undefined &&
      currentId !== undefined &&
      prevAttributeId.current !== currentId
    ) {
      onChange(fieldName, { value: null });
    }
    prevAttributeId.current = currentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attribute?.id]);

  // Reset the value field when the selected operator changes.
  const prevOperatorId = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentId = operator?.id;
    if (
      prevOperatorId.current !== undefined &&
      currentId !== undefined &&
      prevOperatorId.current !== currentId
    ) {
      onChange(fieldName, { value: null });
    }
    prevOperatorId.current = currentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operator?.id]);

  // Dynamic value options. Include matchWith?.id so switching between two
  // API-backed Match With options (each with its own fetchOptions.method)
  // always refetches from the new source, even if two methods share a name.
  const { data: fetchedOptions = [], isFetching } = useQuery({
    queryKey: [
      effectiveFetchOptions?.method.name,
      attribute?.id,
      matchWith?.id,
      effectiveFetchOptions?.params,
    ],
    queryFn: () =>
      effectiveFetchOptions!
        .method(effectiveFetchOptions!.params ?? ({} as never))
        .then((r: unknown[]) => r.map(effectiveFetchOptions!.parseOption)),
    enabled:
      isSelectType &&
      !!effectiveFetchOptions &&
      !effectivePaginatedFetchOptions,
    placeholderData: keepPreviousData,
  });

  // Static valueOptions take priority over dynamic fetch result.
  const options = effectiveValueOptions ?? fetchedOptions;
  const label = config.labels?.value ?? "Value";

  // When Match With is enabled AND this row has Match With options, the value
  // input stays disabled until a Match With option is chosen. Rows with no
  // Match With options are never gated, so existing required-value behavior is
  // preserved. When not gated, valueDisabled === config.readOnly.
  const valueDisabled =
    config.readOnly ||
    !attribute?.id ||
    !operator?.id ||
    (config.enableMatchWith && matchWithHasOptions && !matchWith?.id);

  // Wrap the raw API method with parseOption so FormDropDownListPaginate
  // receives ConditionOption[] items.
  const wrappedPaginatedMethod = useMemo(() => {
    if (!effectivePaginatedFetchOptions) return undefined;
    const { method, parseOption } = effectivePaginatedFetchOptions;
    return async (params: Record<string, unknown>) => {
      const response = await method(params);
      return {
        items: (response.items ?? [])
          .map(parseOption)
          .filter((item): item is ConditionOption => item !== null),
        total: response.total ?? 0,
      };
    };
  }, [effectivePaginatedFetchOptions]);

  if (
    isSelectType &&
    wrappedPaginatedMethod &&
    effectivePaginatedFetchOptions
  ) {
    return (
      <GridLayoutItem>
        <FormDropDownListPaginate
          name={fieldName}
          label={label}
          floatingLabel={true}
          disabled={valueDisabled}
          textField="name"
          dataItemKey="id"
          isMultiValue={true}
          isSingleSelect={effectiveValueType !== "multi-select"}
          method={wrappedPaginatedMethod}
          params={effectivePaginatedFetchOptions.params}
          sort={
            effectivePaginatedFetchOptions.sort ?? { field: "name", dir: "asc" }
          }
          perPage={effectivePaginatedFetchOptions.perPage ?? 20}
          allowCustom={false}
          validator={valueValidator}
        />
      </GridLayoutItem>
    );
  }

  if (isSelectType) {
    return (
      <GridLayoutItem>
        <FormMultiSelect
          name={fieldName}
          label={label}
          floatingLabel={true}
          disabled={valueDisabled}
          data={options}
          textField="name"
          dataItemKey="id"
          isSingleSelect={effectiveValueType === "single-select"}
          loading={isFetching}
          validator={valueValidator}
        />
      </GridLayoutItem>
    );
  }

  if (effectiveValueType === "text-area") {
    return (
      <GridLayoutItem>
        <FormTextArea
          name={fieldName}
          label={label}
          floatingLabel={true}
          disabled={valueDisabled}
          placeholder="Enter value"
          validator={valueValidator}
        />
      </GridLayoutItem>
    );
  }

  return (
    <GridLayoutItem>
      <FormInput
        name={fieldName}
        label={label}
        floatingLabel={true}
        disabled={valueDisabled}
        placeholder="Enter value"
        validator={valueValidator}
      />
    </GridLayoutItem>
  );
};

export default ConditionValueField;
